<script lang="ts" setup>
import 'viewerjs/dist/viewer.css'
import * as viewer from 'v-viewer'
import { getUploadedFile } from '~/components/File/common'
import type { FilePath, UploadedFile } from '~/types'

const props = withDefaults(defineProps<{
  files?: FilePath[]
  imageClass?: string
  withPreview?: boolean
  isLoading?: boolean
  /**
   * Lazy loads files when set to true. Loads the current and adjacent files in the carousel.
   * @default false
   */
  lazyLoadFiles?: boolean
  /**
   * If defined, only displays this number of dots and "rotate" through all indicator dots.
   * If left undefined, X dots for X images are displayed.
   */
  maxNumberOfDots?: number
  dotColor?: 'white' | 'hos-blue'
}>(), {
  lazyLoadFiles: false,
  dotColor: 'white',
})
const uploadedFiles = computed((): UploadedFile[] => props.files ? props.files.map(file => getUploadedFile(file)) : [])

// Image Preview
const photoPaths = computed(() => uploadedFiles.value.map(({ filekey }) => filekey))

function previewImgObject(index: number) {
  viewer.api({
    images: photoPaths.value.map(photo => ({
      'data-source': photo,
      'src': photo,
    })),
    options: {
      toolbar: true,
      url: 'data-source',
      initialViewIndex: index,
      movable: false,
    },
  })
}

const currentCarouselIndex = ref(0)

function isSameOrAdjacentIndex(a: number, b: number) {
  const highestIndex = uploadedFiles.value.length - 1

  if (a === b) {
    return true
  }

  const nextIndex = a < highestIndex ? a + 1 : 0
  const previousIndex = a > 0 ? a - 1 : highestIndex

  return b === nextIndex || b === previousIndex
}

const loadedFiles = ref<number[]>([])
function deferLoadFile(fileIndex: number) {
  return !!props.lazyLoadFiles && !loadedFiles.value.includes(fileIndex) && !isSameOrAdjacentIndex(fileIndex, currentCarouselIndex.value)
}

// Generated via https://blurha.sh/ with an image of a forklift provided by HOS
const FALLBACK_BLURHASH = 'LkHCG[D%x]WV?wRjs:WB-;xuV@of'
</script>

<template>
  <n-skeleton
    v-if="isLoading"
    :class="imageClass ? imageClass : 'DefaultImageSize'"
    :sharp="false"
  />
  <CustomerModuleImagePlaceholder
    v-else-if="uploadedFiles.length === 0"
    class="text-2xl"
    :class="imageClass ? imageClass : 'DefaultImageSize'"
  />
  <n-carousel
    v-else
    v-model:current-index="currentCarouselIndex"
    show-arrow
    :space-between="20"
    :draggable="props.files && props.files?.length > 1"
    :touchable="props.files && props.files?.length > 1"
    class="rounded min-h-32"
  >
    <!-- Do not wrap this <UnLazyImage> in another element, such as a <div>!
    When doing so, it leads to the last image of the carousel not rendering correctly anymore.
    For more context on the matter, read https://github.com/sidestream-tech/hanselmann-os/pull/3512#issuecomment-2414495924
    -->
    <UnLazyImage
      v-for="(file, index) in uploadedFiles"
      :key="file.filekey"
      :src="file.filekey"
      :alt="file.filekey"
      :lazy-load="!deferLoadFile(index)"
      :blurhash="FALLBACK_BLURHASH"
      class="object-contain transition-opacity delay-150"
      :class="[
        imageClass ?? 'DefaultImageSize',
        // this delayed opacity transition avoids an unsightly flickering of unloaded images when directly jumping to an image that was not yet loaded
        { 'opacity-0': deferLoadFile(index) },
      ]"
      @loaded="() => loadedFiles = [...loadedFiles, index]"
    />

    <template #arrow="{ total, currentIndex, prev, next }">
      <div v-if="total > 1" class="absolute bottom-3 right-3 space-x-2">
        <n-button size="large" type="primary" class="size-10" @click="prev">
          <template #icon>
            <Icon name="material-symbols:chevron-left-rounded" class="text-white" />
          </template>
        </n-button>
        <n-button size="large" type="primary" class="size-10" @click="next">
          <template #icon>
            <Icon name="material-symbols:chevron-right-rounded" class="text-white" />
          </template>
        </n-button>
      </div>
      <div class="absolute top-3 right-3">
        <n-button size="large" type="primary" class="size-10" @click="previewImgObject(currentIndex)">
          <template #icon>
            <Icon name="material-symbols:fullscreen-rounded" class="text-white" />
          </template>
        </n-button>
      </div>
    </template>

    <template #dots="{ total, currentIndex, to }">
      <CustomerModuleCarouselCustomDots :total :current-index :to :max-number-of-dots="props.maxNumberOfDots" :dot-color />
    </template>
  </n-carousel>

  <div v-if="withPreview" class="grid grid-cols-6 sm:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 items-center gap-3 mt-4">
    <div v-for="(file, index) in uploadedFiles" :key="file.filekey">
      <UnLazyImage
        :src="file.filekey"
        :alt="file.filekey"
        :lazy-load="true"
        :blurhash="FALLBACK_BLURHASH"
        class="w-full aspect-square object-contain bg-gray-200 rounded cursor-pointer"
        :class="{
          'ring-2 ring-hos-blue': currentCarouselIndex === index,
          'hover:ring-2 ring-gray-400': currentCarouselIndex !== index,
        }"
        @click="currentCarouselIndex = index"
      />
    </div>
  </div>
</template>

<style scoped>
.DefaultImageSize {
  @apply w-full h-[200px] md:h-[350px] 2xl:h-[400px];
}
</style>
